import moment from 'moment';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { Equipment } from '../../types/equipment';
import { DateModel, JsonDateModel } from '../primitive/date.model';
import { JsonNumberModel, NumberModel } from '../primitive/number.model';
import { JsonTextModel, TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { isDefined } from '../../utilities/is-defined';

export class EquipmentModel extends CompositeModel {
    public equnr: TextModel;
    public tidnr: TextModel;
    public mapar: TextModel;
    public matnr: TextModel;
    public sernr: TextModel;
    public warrantyEndDate: DateModel;
    public division: TextModel;
    public equiShortDesc: TextModel;
    public contractType: TextModel;
    public mk: TextModel;
    public tk: TextModel;
    public measuringPoint: TextModel;
    public measuringPointDesc: TextModel;
    public intMerkmal: TextModel;
    public extUnit: TextModel;
    public mandatory: TextModel;
    public serviceKey: TextModel;
    public barcodeMatnr: TextModel;
    public barcodeSernr: TextModel;
    public ipAddress: TextModel;
    public hostId: TextModel;
    public dongle: TextModel;
    public macAddress: TextModel;
    public mkV2: TextModel;
    public softwareVers: TextModel;
    public endOfDeliveryDate: DateModel;
    public endOfSupportDate: DateModel;
    public mainEquipmentIndicator: TextModel;
    public pos: NumberModel;

    /**
     * Represents a part of a part set in a notification
     *
     * @param equnr
     * @param tidnr
     * @param mapar
     * @param matnr
     * @param sernr
     * @param warrantyEndDate
     * @param division
     * @param equiShortDesc
     * @param contractType
     * @param mk
     * @param tk
     * @param measuringPoint
     * @param measuringPointDesc
     * @param intMerkmal
     * @param extUnit
     * @param mandatory
     * @param serviceKey
     * @param barcodeMatnr
     * @param barcodeSernr
     * @param ipAddress
     * @param hostId
     * @param dongle
     * @param macAddress
     * @param mkV2
     * @param softwareVers
     * @param endOfDeliveryDate
     * @param endOfSupportDate
     * @param mainEquipmentIndicator
     * @param pos
     * @class
     */
    public constructor(
        equnr: string,
        tidnr: string,
        mapar: string,
        matnr: string,
        sernr: string,
        warrantyEndDate: moment.Moment | undefined,
        division: string,
        equiShortDesc: string,
        contractType: string,
        mk: string,
        tk: string,
        measuringPoint: string,
        measuringPointDesc: string,
        intMerkmal: string,
        extUnit: string,
        mandatory: string,
        serviceKey: string,
        barcodeMatnr: string,
        barcodeSernr: string,
        ipAddress: string,
        hostId: string,
        dongle: string,
        macAddress: string,
        mkV2: string,
        softwareVers: string,
        endOfDeliveryDate: moment.Moment | undefined,
        endOfSupportDate: moment.Moment | undefined,
        mainEquipmentIndicator: string,
        pos: number,
    ) {
        super('EquipmentModel');

        this.equnr = <TextModel>this.set(new TextModel('Equnr', equnr));
        this.tidnr = <TextModel>this.set(new TextModel('Tidnr', tidnr));
        this.mapar = <TextModel>this.set(new TextModel('Mapar', mapar));
        this.matnr = <TextModel>this.set(new TextModel('Matnr', matnr));
        this.sernr = <TextModel>this.set(new TextModel('Sernr', sernr));
        this.warrantyEndDate = <DateModel>this.set(new DateModel('WarrantyEndDate', warrantyEndDate));
        this.division = <TextModel>this.set(new TextModel('Division', division));
        this.equiShortDesc = <TextModel>this.set(new TextModel('EquiShortDesc', equiShortDesc));
        this.contractType = <TextModel>this.set(new TextModel('ContractType', contractType));
        this.mk = <TextModel>this.set(new TextModel('Mk', mk));
        this.tk = <TextModel>this.set(new TextModel('Tk', tk));
        this.measuringPoint = <TextModel>this.set(new TextModel('MeasuringPoint', measuringPoint));
        this.measuringPointDesc = <TextModel>this.set(new TextModel('MeasuringPointDesc', measuringPointDesc));
        this.intMerkmal = <TextModel>this.set(new TextModel('IntMerkmal', intMerkmal));
        this.extUnit = <TextModel>this.set(new TextModel('ExtUnit', extUnit));
        this.mandatory = <TextModel>this.set(new TextModel('Mandatory', mandatory));
        this.serviceKey = <TextModel>this.set(new TextModel('ServiceKey', serviceKey));
        this.barcodeMatnr = <TextModel>this.set(new TextModel('BarcodeMatnr', barcodeMatnr));
        this.barcodeSernr = <TextModel>this.set(new TextModel('BarcodeSernr', barcodeSernr));
        this.ipAddress = <TextModel>this.set(new TextModel('IpAdr', ipAddress));
        this.hostId = <TextModel>this.set(new TextModel('HostId', hostId));
        this.dongle = <TextModel>this.set(new TextModel('Dongle', dongle));
        this.macAddress = <TextModel>this.set(new TextModel('MacAdresse', macAddress));
        this.mkV2 = <TextModel>this.set(new TextModel('MkV2', mkV2));
        this.softwareVers = <TextModel>this.set(new TextModel('SoftwareVers', softwareVers));
        this.endOfDeliveryDate = <DateModel>this.set(new DateModel('EndOfDeliveryDate', endOfDeliveryDate));
        this.endOfSupportDate = <DateModel>this.set(new DateModel('EndOfSupportDate', endOfSupportDate));
        this.mainEquipmentIndicator = <TextModel>this.set(new TextModel('MEIndicator', mainEquipmentIndicator));
        this.pos = <NumberModel>this.set(new NumberModel('Pos', pos));
    }

    public static returnFormattedServiceKey(unformattedServiceKey: string | undefined): FormattedServiceKey {
        if (!isDefined(unformattedServiceKey)) {
            return {
                primaryText: '',
                emphasizedText: '',
            };
        }
        const charLength = unformattedServiceKey.length;
        const serviceKeyLimits = this.getServiceKeyLimits();
        const sKeyLowerLimit = serviceKeyLimits.serviceKeyLowerLimit;
        const sKeyUpperLimit = serviceKeyLimits.serviceKeyUpperLimit;
        const sKeyDifferenceBetweenLimits = serviceKeyLimits.serviceKeyDifferenceBetweenLimits;
        const sKeyDifferenceOutOfLimits = serviceKeyLimits.serviceKeyDifferenceOutOfLimits;

        let formattedText = {
            primaryText: unformattedServiceKey,
            emphasizedText: '',
        };
        if (charLength >= sKeyLowerLimit && charLength < sKeyUpperLimit) {
            formattedText = {
                primaryText: unformattedServiceKey.slice(
                    0,
                    charLength - serviceKeyLimits.serviceKeyDifferenceBetweenLimits,
                ),
                emphasizedText: unformattedServiceKey.slice(-sKeyDifferenceBetweenLimits),
            };
        }

        if (charLength >= serviceKeyLimits.serviceKeyUpperLimit) {
            formattedText = {
                primaryText: unformattedServiceKey.slice(0, charLength - sKeyDifferenceOutOfLimits),
                emphasizedText: unformattedServiceKey.slice(-sKeyDifferenceOutOfLimits),
            };
        }
        return formattedText;
    }

    public static getServiceKeyLimits(): ServiceKeyLimits {
        const sKeyLowerLimit = 20;
        const sKeyUpperLimit = 280;
        const sKeyDifferenceBetweenLimits = 6;
        const sKeyDifferenceOutOfLimits = 11;

        const serviceKeyLimits = {
            serviceKeyLowerLimit: sKeyLowerLimit,
            serviceKeyUpperLimit: sKeyUpperLimit,
            serviceKeyDifferenceBetweenLimits: sKeyDifferenceBetweenLimits,
            serviceKeyDifferenceOutOfLimits: sKeyDifferenceOutOfLimits,
        };

        return serviceKeyLimits;
    }

    public static fromOData(eq: ODataEquipmentModel): EquipmentModel {
        return new EquipmentModel(
            eq.EquipmentNum,
            eq.TechIdentNum,
            eq.ManPartNum,
            eq.MaterialNum,
            eq.SerialNum,
            moment(eq.WarrantyEndDate),
            eq.Division,
            eq.EquiShortDesc,
            eq.ContractType,
            eq.Mk,
            eq.Tk,
            eq.MeasuringPoint,
            eq.MeasuringPointDesc,
            eq.IntCharacteristic,
            eq.ExtUnit,
            eq.Mandatory,
            eq.ServiceKey,
            eq.BarcodeMaterial,
            eq.BarcodeSernr,
            eq.IpAddress,
            eq.HostId,
            eq.Dongle,
            eq.MacAdresse,
            eq.MkV2,
            eq.SoftwareVers,
            moment(eq.EndOfDeliveryDate),
            moment(eq.EndOfSupportDate),
            eq.MEIndicator,
            eq.Counter,
        );
    }

    public getEquipmentState(): Equipment {
        return {
            equnr: this.equnr.value,
            tidnr: this.tidnr.value,
            matnr: this.matnr.value,
            sernr: this.sernr.value,
            equiShortDesc: this.equiShortDesc.value,
            division: this.division.value,
            mainEquipmentIndicator: this.mainEquipmentIndicator.value,
        };
    }
}

export class EquipmentModelFromJson extends EquipmentModel {
    public constructor(data: JsonEquipmentModel) {
        super(
            data.equnr.value,
            data.tidnr.value,
            data.mapar.value,
            data.matnr.value,
            data.sernr.value,
            data.warrantyEndDate.value ? moment(data.warrantyEndDate.value) : undefined,
            data.division.value,
            data.equiShortDesc.value,
            data.contractType.value,
            data.mk.value,
            data.tk.value,
            data.measuringPoint.value,
            data.measuringPointDesc.value,
            data.intMerkmal.value,
            data.extUnit.value,
            data.mandatory.value,
            data.serviceKey.value,
            data.barcodeMatnr.value,
            data.barcodeSernr.value,
            data.ipAddress.value,
            data.hostId.value,
            data.dongle.value,
            data.macAddress.value,
            data.mkV2.value,
            data.softwareVers.value,
            data.endOfDeliveryDate.value ? moment(data.endOfDeliveryDate.value) : undefined,
            data.endOfSupportDate.value ? moment(data.endOfSupportDate.value) : undefined,
            data.mainEquipmentIndicator.value,
            data.pos.value,
        );
    }
}

export interface ServiceKeyLimits {
    serviceKeyLowerLimit: number;
    serviceKeyUpperLimit: number;
    serviceKeyDifferenceBetweenLimits: number;
    serviceKeyDifferenceOutOfLimits: number;
}

export interface ODataEquipmentModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: 'HC_SM_NGM_58_SRV.Equipments';
    };
    EquipmentNum: string;
    TechIdentNum: string;
    MEIndicator: string;
    ManPartNum: string;
    MaterialNum: string;
    SerialNum: string;
    WarrantyEndDate: string;
    Division: string;
    EquiShortDesc: string;
    ContractType: string;
    Mk: string;
    Tk: string;
    MeasuringPoint: string;
    MeasuringPointDesc: string;
    IntCharacteristic: string;
    ExtUnit: string;
    Mandatory: string;
    ServiceKey: string;
    BarcodeMaterial: string;
    BarcodeSernr: string;
    IpAddress: string;
    HostId: string;
    Dongle: string;
    MacAdresse: string;
    MkV2: string;
    SoftwareVers: string;
    EndOfDeliveryDate: string;
    EndOfSupportDate: string;
    Counter: number;
}

export interface JsonEquipmentModel {
    equnr: JsonTextModel;
    tidnr: JsonTextModel;
    mapar: JsonTextModel;
    matnr: JsonTextModel;
    sernr: JsonTextModel;
    warrantyEndDate: JsonDateModel;
    division: JsonTextModel;
    equiShortDesc: JsonTextModel;
    contractType: JsonTextModel;
    mk: JsonTextModel;
    tk: JsonTextModel;
    measuringPoint: JsonTextModel;
    measuringPointDesc: JsonTextModel;
    intMerkmal: JsonTextModel;
    extUnit: JsonTextModel;
    mandatory: JsonTextModel;
    serviceKey: JsonTextModel;
    barcodeMatnr: JsonTextModel;
    barcodeSernr: JsonTextModel;
    ipAddress: JsonTextModel;
    hostId: JsonTextModel;
    dongle: JsonTextModel;
    macAddress: JsonTextModel;
    mkV2: JsonTextModel;
    softwareVers: JsonTextModel;
    endOfDeliveryDate: JsonDateModel;
    endOfSupportDate: JsonDateModel;
    mainEquipmentIndicator: JsonTextModel;
    pos: JsonNumberModel;
}

export interface ODataServiceKeyModel {
    __metadata: {
        id: string;
        uri: string;
        type: 'HC_SM_NGM_58_SRV.ServKey';
    };
    NotificationNo: string;
    FrontId: string;
    HardwareId: string;
    Material: string;
    SerialNum: string;
    ServiceKey: string;
    ReturnCode: number;
    ErrorText: string;
}

export interface FormattedServiceKey {
    primaryText: string;
    emphasizedText: string;
}
